import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../API';
import styles from './GirderConnection.module.css';

/**
 * Confirm renumbering a connected spoke's network numeric ID.
 */
export default function RenumberNodeModal({
    open,
    onClose,
    connectionId,
    currentNumericId,
    currentIdentifier,
    networkName,
    peerName,
}) {
    const queryClient = useQueryClient();
    const [numericId, setNumericId] = useState(
        currentNumericId != null ? String(currentNumericId) : ''
    );
    const [confirmText, setConfirmText] = useState('');
    const [error, setError] = useState(null);

    const previewIdentifier = useMemo(() => {
        const n = parseInt(numericId, 10);
        if (!networkName || Number.isNaN(n) || n < 1) return null;
        return `${networkName}${String(n).padStart(2, '0')}`;
    }, [networkName, numericId]);

    const confirmed = previewIdentifier && confirmText.trim() === previewIdentifier;

    const renumberMutation = useMutation({
        mutationFn: async () => {
            const response = await api.post(`network/connection/${connectionId}/renumber`, {
                query: { numericId: parseInt(numericId, 10) },
            });
            if (response.status !== 200 || response.data?.success === false) {
                throw new Error(response.data?.error || 'Renumber failed');
            }
            return response.data;
        },
        onSuccess: async (data) => {
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['local-connections'] });
            await queryClient.invalidateQueries({ queryKey: ['hub-allocation'] });
            if (data?.hubSyncScheduled === false && data?.hubSyncError) {
                alert(
                    `Spoke identity updated to ${data.newIdentifier}, but hub sync failed: ${data.hubSyncError}`
                );
            } else {
                alert(
                    `Renumbered ${peerName || 'spoke'} to ${data.newIdentifier}. ` +
                    'Identity propagation is running on the spoke; hub sync was scheduled.'
                );
            }
            onClose();
        },
        onError: (err) => {
            setError(err.message || 'Renumber failed');
        },
    });

    if (!open) return null;

    const modal = (
        <div
            className={styles.autoSyncDialogOverlay}
            onClick={() => !renumberMutation.isPending && onClose()}
            role="presentation"
        >
            <div
                className={styles.autoSyncDialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="renumber-node-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.autoSyncDialogHeader}>
                    <h3 id="renumber-node-title" className={styles.autoSyncDialogTitle}>
                        Change node ID
                    </h3>
                    <button
                        type="button"
                        className={styles.autoSyncDialogClose}
                        onClick={onClose}
                        disabled={renumberMutation.isPending}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <div className={styles.autoSyncDialogBody}>
                    <div className={styles.autoSyncDialogField}>
                        <p className={styles.autoSyncDialogCopy}>
                            Current ID for <strong>{peerName || 'spoke'}</strong>:{' '}
                            <code>{currentIdentifier || '—'}</code>
                            {currentNumericId != null ? ` (#${currentNumericId})` : ''}
                        </p>
                        <p className={styles.autoSyncDialogCopy}>
                            This renames BDSA cases/folders/items on the spoke and refreshes
                            hub mirrors. It can take several minutes. Avoid syncing this
                            connection until the jobs finish.
                        </p>
                    </div>
                    <div className={styles.autoSyncDialogField}>
                        <label htmlFor="renumber-numeric-id">New numeric ID</label>
                        <input
                            id="renumber-numeric-id"
                            type="number"
                            min={1}
                            value={numericId}
                            onChange={(e) => {
                                setNumericId(e.target.value);
                                setConfirmText('');
                                setError(null);
                            }}
                        />
                        {previewIdentifier && (
                            <p className={styles.autoSyncDialogCopy}>
                                Preview identifier: <strong>{previewIdentifier}</strong>
                            </p>
                        )}
                    </div>
                    <div className={styles.autoSyncDialogField}>
                        <label htmlFor="renumber-confirm">
                            Type <code>{previewIdentifier || '…'}</code> to confirm
                        </label>
                        <input
                            id="renumber-confirm"
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            disabled={!previewIdentifier}
                            placeholder={previewIdentifier || 'Enter new ID first'}
                            autoComplete="off"
                        />
                    </div>
                    {error && (
                        <div className={styles.inlineActionError}>{error}</div>
                    )}
                </div>
                <div className={styles.autoSyncDialogFooter}>
                    <button
                        type="button"
                        className={styles.syncButtonSecondary}
                        disabled={renumberMutation.isPending}
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.autoSyncDialogDone}
                        disabled={!confirmed || renumberMutation.isPending}
                        onClick={() => renumberMutation.mutate()}
                    >
                        {renumberMutation.isPending ? 'Renumbering…' : 'Renumber'}
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
